import React, { useCallback, useEffect, useState } from 'react';
import { IORGDataTableQueryState } from 'shared-it-appmod-ui';
import Moment from 'src/libraries/moment.library';
import history from 'src/history';
import {
  getFireDispatchStatus,
  getTableListParams,
  getCSVContent,
} from 'src/selectors/fire-dispatch.selector';
import { actionTypes } from 'src/ducks/fire-dispatch.duck';
import useTabLocation from 'src/hooks/tab-location.hook';
import { DispatchStatuses } from 'src/constants';
import { useFireAgencyContext } from 'src/contexts/fire-agency.context';
import { useFireDispatchContext } from 'src/contexts/fire-dispatch.context';
import Lang from 'src/libraries/language';
import FireDispatchDetailsModal from '../fire-dispatch-details/fire-dispatch-details.component';
import FlightRequestListView from './fire-dispatch-list.view';

const FlightDispatchList: React.FC = () => {
  const {
    state: fireAgencystateState,
    actions: fireAgencystateActions,
  } = useFireAgencyContext();
  const { state, actions } = useFireDispatchContext();
  const { currentTab, params } = useTabLocation();
  const [openDetails, setOpenDetails] = useState<{
    open: boolean;
    dispatchId?: number | null;
  }>({ open: false });
  const [queryParams, setQueryParams] = useState<IORGDataTableQueryState>({
    page: 0,
    limit: 0,
  });
  const [dateRange, setDateRange] = useState(
    `01/01/${params.get('year') ??
      Moment().format('YYYY')} - 12/31/${params.get('year') ??
      Moment().format('YYYY')}`
  );

  const closeDetailsModal = () => {
    params.delete('dispatchId');
    history.push(`/dispatch?${params.toString()}`);
    setOpenDetails({ open: false });
  };
  const openDetailsModal = useCallback(
    (dispatchId?: number | null) => {
      if (dispatchId) {
        setOpenDetails({ open: true, dispatchId });
        params.delete('dispatchId');
        history.push(`/dispatch?${params.toString()}&dispatchId=${dispatchId}`);
      } else {
        const id = Number(params.get('dispatchId')) ?? 0;
        if (id > 0) {
          setOpenDetails({ open: true, dispatchId: id });
        }
      }
    },
    [params]
  );

  useEffect(() => {
    actions.filterGET();
    fireAgencystateActions.listGET();
    openDetailsModal();
  }, [actions, fireAgencystateActions]);

  const handleFetch = useCallback(
    (listParams: IORGDataTableQueryState) => {
      const urlParams = { ...listParams, ...Object.fromEntries(params) };
      if (JSON.stringify(queryParams) !== JSON.stringify(urlParams)) {
        const data = getTableListParams(urlParams, currentTab);
        actions.listGET(data);
        localStorage.setItem('status', data.status);
        setQueryParams(urlParams);
      }
    },
    [actions, currentTab, queryParams, params]
  );

  const filters = {
    ...state.filters,
    fireAgencies: fireAgencystateState.list.map(
      (fireAgency) => fireAgency.name
    ),
  };

  const handleDownload = useCallback(
    async (listParams: IORGDataTableQueryState) => {
      const { page, limit, ...data } = getTableListParams(
        listParams,
        currentTab
      );
      data.limit = 0;

      const result = await actions.reportListGET(data);
      if (result.payload && result.payload.report.rows.length) {
        const items = result.payload.report.rows.map((value) => [
          Moment(value.dispatchDate).format('MM/DD/YYYY'),
          value.dispatchTime,
          value.liftoffTime,
          value.responseTime,
          value.returnTime,
          value.flightHrs,
          value.dipCount,
          value.waterGallons,
          value.fireName,
          value.burnedAcres,
          value.agencyName,
          value.status,
        ]);

        const content = getCSVContent(items, [
          Lang.LBL_DISPATCH_DATE,
          Lang.LBL_DISPATCH_TIME,
          Lang.LBL_LIFTOFF_TIME,
          Lang.LBL_RESPONSE_TIME,
          Lang.LBL_FIRE_DISPATCH_RETURN_TIME,
          Lang.LBL_FLIGHT_HOURS,
          Lang.LBL_NUMBER_OF_DIPS,
          Lang.LBL_GALLONS_DROPPED,
          Lang.LBL_FIRE_NAME_COMMENTS,
          Lang.LBL_ACRES_BURNED,
          Lang.LBL_FIRE_AGENCY,
          Lang.LBL_FLIGHT_STATUS,
        ]);

        const link = document.createElement('a');
        link.setAttribute('href', content);
        link.setAttribute(
          'download',
          `Dispatch_Report_${Moment().format('YYYYMMDD')}.xls`
        );
        document.body.appendChild(link);

        link.click();
        document.body.removeChild(link);
      }
    },
    [currentTab, actions]
  );

  let total = 0;

  switch (localStorage.getItem('status')) {
    case DispatchStatuses.Cancelled: {
      total = state.cancelled;
      break;
    }

    case DispatchStatuses.Dispatched: {
      total = state.dispatched;
      break;
    }

    case DispatchStatuses.InFlight: {
      total = state.inFlight;
      break;
    }

    case DispatchStatuses.Landed: {
      total = state.landed;
      break;
    }

    case DispatchStatuses.Completed: {
      total = state.completed;
      break;
    }

    default: {
      total = state.total;
      break;
    }
  }

  return (
    <>
      <FlightRequestListView
        data={state.list}
        filters={filters}
        handleFetch={handleFetch}
        handleDownload={handleDownload}
        total={state.total}
        cancelled={state.cancelled}
        completed={state.completed}
        dispatched={state.dispatched}
        inFlight={state.inFlight}
        landed={state.landed}
        totalItems={total}
        dateRange={dateRange}
        loading={
          getFireDispatchStatus(state, actionTypes.FIRE_DISPATCH_FETCH_DATA)
            .fetching
        }
        downloading={
          getFireDispatchStatus(state, actionTypes.FIRE_DISPATCH_REPORT_DATA)
            .fetching
        }
        openDetailsModal={openDetailsModal}
        handleDateRange={(value) => setDateRange(value)}
      />
      <FireDispatchDetailsModal {...openDetails} onClose={closeDetailsModal} />
    </>
  );
};

export default FlightDispatchList;
